import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";

export function CheckboxGroup({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      data-slot="control"
      {...props}
      className={clsx(
        className,
        // Basic groups
        "space-y-3",
        // With descriptions
        "has-data-[slot=description]:space-y-6 has-data-[slot=description]:**:data-[slot=label]:font-medium"
      )}
    />
  );
}

export function CheckboxField({
  className,
  ...props
}: { className?: string } & Omit<Headless.FieldProps, "as" | "className">) {
  return (
    <Headless.Field
      data-slot="field"
      {...props}
      className={clsx(
        className,
        // Base layout
        "grid grid-cols-[1.125rem_1fr] gap-x-4 gap-y-1 sm:grid-cols-[1rem_1fr]",
        // Control layout
        "*:data-[slot=control]:col-start-1 *:data-[slot=control]:row-start-1 *:data-[slot=control]:mt-0.75 sm:*:data-[slot=control]:mt-1",
        // Label layout
        "*:data-[slot=label]:col-start-2 *:data-[slot=label]:row-start-1",
        // Description layout
        "*:data-[slot=description]:col-start-2 *:data-[slot=description]:row-start-2",
        // With description
        "has-data-[slot=description]:**:data-[slot=label]:font-medium"
      )}
    />
  );
}

// This project runs Tailwind v3.4, not v4: bare boolean data-variants like
// `group-data-checked:` are v4-only shorthand and silently generate no CSS
// here — v3 needs the bracket form `group-data-[checked]:`. Colors are static
// utility classes rather than the upstream kit's `bg-(--checkbox-checked-bg)`
// CSS-variable indirection for the same reason: v3 has no `--color-*` custom
// properties for its palette (those are a v4 default), so `var(--color-...)`
// resolved to nothing. Unlike e.g. Badge's `group-data-hover:` (a no-op here
// too, but it only skips a hover polish with a normal-looking fallback), an
// unchecked and a checked box have NO other visual difference — so here the
// same silent no-op meant checked never looked different from unchecked.
const base = [
  // Basic layout
  "relative isolate flex size-[1.125rem] items-center justify-center rounded-[0.3125rem] sm:size-4",
  // Unchecked surface
  "border shadow-sm border-zinc-950/15 bg-white dark:border-white/15 dark:bg-white/5",
  "group-data-[hover]:border-zinc-950/30 dark:group-data-[hover]:border-white/30",
  // Checked: colors[color] below sets the fill and border; only drop the shadow here
  "group-data-[checked]:shadow-none",
  // Inner highlight shadow (unchecked, light mode only)
  "after:absolute after:inset-0 after:rounded-[calc(0.3125rem-1px)] after:shadow-[inset_0_1px_0_rgb(255_255_255_/_15%)]",
  "dark:after:hidden",
  // Focus ring
  "group-data-[focus]:outline-2 group-data-[focus]:outline-offset-2 group-data-[focus]:outline-blue-500",
  // Disabled state
  "group-data-[disabled]:opacity-50 group-data-[disabled]:shadow-none",
  "group-data-[disabled]:border-zinc-950/25 group-data-[disabled]:bg-zinc-950/5",
  "dark:group-data-[disabled]:border-white/20 dark:group-data-[disabled]:bg-white/[0.025]",
  // Forced colors mode
  "forced-colors:group-data-[checked]:bg-[Highlight] forced-colors:group-data-[checked]:border-[Highlight]",
];

/** Checked-state fill + border, per color. */
const colors = {
  "dark/zinc":
    "group-data-[checked]:border-zinc-950/90 group-data-[checked]:bg-zinc-900 dark:group-data-[checked]:border-zinc-950/90 dark:group-data-[checked]:bg-zinc-600",
  "dark/white":
    "group-data-[checked]:border-zinc-950/90 group-data-[checked]:bg-zinc-900 dark:group-data-[checked]:border-zinc-950/15 dark:group-data-[checked]:bg-white",
  white: "group-data-[checked]:border-zinc-950/15 group-data-[checked]:bg-white",
  dark: "group-data-[checked]:border-zinc-950/90 group-data-[checked]:bg-zinc-900",
  zinc: "group-data-[checked]:border-zinc-700/90 group-data-[checked]:bg-zinc-600",
  red: "group-data-[checked]:border-red-700/90 group-data-[checked]:bg-red-600",
  orange: "group-data-[checked]:border-orange-600/90 group-data-[checked]:bg-orange-500",
  amber: "group-data-[checked]:border-amber-500/80 group-data-[checked]:bg-amber-400",
  yellow: "group-data-[checked]:border-yellow-400/80 group-data-[checked]:bg-yellow-300",
  lime: "group-data-[checked]:border-lime-400/80 group-data-[checked]:bg-lime-300",
  green: "group-data-[checked]:border-green-700/90 group-data-[checked]:bg-green-600",
  emerald: "group-data-[checked]:border-emerald-700/90 group-data-[checked]:bg-emerald-600",
  teal: "group-data-[checked]:border-teal-700/90 group-data-[checked]:bg-teal-600",
  cyan: "group-data-[checked]:border-cyan-400/80 group-data-[checked]:bg-cyan-300",
  sky: "group-data-[checked]:border-sky-600/80 group-data-[checked]:bg-sky-500",
  blue: "group-data-[checked]:border-blue-700/90 group-data-[checked]:bg-blue-600",
  indigo: "group-data-[checked]:border-indigo-600/90 group-data-[checked]:bg-indigo-500",
  violet: "group-data-[checked]:border-violet-600/90 group-data-[checked]:bg-violet-500",
  purple: "group-data-[checked]:border-purple-600/90 group-data-[checked]:bg-purple-500",
  fuchsia: "group-data-[checked]:border-fuchsia-600/90 group-data-[checked]:bg-fuchsia-500",
  pink: "group-data-[checked]:border-pink-600/90 group-data-[checked]:bg-pink-500",
  rose: "group-data-[checked]:border-rose-600/90 group-data-[checked]:bg-rose-500",
};

/** Checkmark stroke color, per color — light fills need a dark mark. */
const checkStrokeColors: Record<keyof typeof colors, string> = {
  "dark/zinc": "stroke-white",
  "dark/white": "stroke-white dark:stroke-zinc-900",
  white: "stroke-zinc-900",
  dark: "stroke-white",
  zinc: "stroke-white",
  red: "stroke-white",
  orange: "stroke-white",
  amber: "stroke-amber-950",
  yellow: "stroke-yellow-950",
  lime: "stroke-lime-950",
  green: "stroke-white",
  emerald: "stroke-white",
  teal: "stroke-white",
  cyan: "stroke-cyan-950",
  sky: "stroke-white",
  blue: "stroke-white",
  indigo: "stroke-white",
  violet: "stroke-white",
  purple: "stroke-white",
  fuchsia: "stroke-white",
  pink: "stroke-white",
  rose: "stroke-white",
};

type Color = keyof typeof colors;

export function Checkbox({
  color = "dark/zinc",
  className,
  ...props
}: {
  color?: Color;
  className?: string;
} & Omit<Headless.CheckboxProps, "as" | "className">) {
  return (
    <Headless.Checkbox
      data-slot="control"
      {...props}
      className={clsx(className, "group inline-flex focus:outline-hidden")}
    >
      <span className={clsx(base, colors[color])}>
        <svg
          className={clsx(
            "size-4 opacity-0 group-data-[checked]:opacity-100 sm:h-3.5 sm:w-3.5",
            checkStrokeColors[color]
          )}
          viewBox="0 0 14 14"
          fill="none"
        >
          {/* Checkmark icon */}
          <path
            className="opacity-100 group-data-[indeterminate]:opacity-0"
            d="M3 8L6 11L11 3.5"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Indeterminate icon */}
          <path
            className="opacity-0 group-data-[indeterminate]:opacity-100"
            d="M3 7H11"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </Headless.Checkbox>
  );
}
